import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Center, Environment, Lightformer, useAnimations, useGLTF, useTexture } from '@react-three/drei'
import * as THREE from 'three'

// The pack model and its default artwork. The GLB is fetched at runtime by
// GLTFLoader, so it comes in through Parcel's `url:` pipeline (a copied file +
// URL) rather than being bundled into the JS. The webps are ordinary image
// imports, which Parcel already resolves to URLs.
import packModelUrl from 'url:../assets/slabz-pack.glb'
import pack3Front from '../assets/pack3-front.webp'
import pack3Back from '../assets/pack3-back.webp'

// The single baked clip on the model: a full turntable rotation of the solid
// pack (front -> edge -> back -> front). Named exactly as authored in Blender;
// a mismatch just means the "open" flourish silently does nothing.
const OPEN_CLIP = 'Box006Action'

// The two material slots that carry the pack's face art, by their authored
// names. Slot 0 is the front, slot 1 the back; the third material is the foil
// edge, left untouched. Targeting by name rather than index so a re-export
// that reorders primitives cannot silently swap the faces.
const FRONT_MATERIAL = 'pack_texture_02.001'
const BACK_MATERIAL = 'pack_texture_02_back.001'

useGLTF.preload(packModelUrl)

// Shared, mutable orientation the DOM pointer handlers write and the render
// loop reads. A ref (not state) so a drag can update it every pointer event
// without re-rendering React on each frame.
type Orient = {
    rotY: number
    rotX: number
    dragging: boolean
    opening: boolean
    /** Radians per second still to be spent after the finger lifts. A flick
     *  should keep turning and slow down, which is most of what makes the Slabz
     *  pack feel like an object rather than a picture. */
    velY: number
    /** Seconds since the pack was last touched. The idle sway fades back in
     *  from here, so it does not snap on the instant a drag ends. */
    idleFor: number
    /** Where the sway resumes from, so it picks up at the angle the player left
     *  the pack at instead of jumping to wherever the sine happens to be. */
    restY: number
    /** Clock time at which the pack came to rest. The sway's phase is measured
     *  from here, so it starts at zero offset and the pack does not step the
     *  instant it goes idle. */
    idlePhase: number
}

type ModelProps = {
    frontArt: string
    backArt: string
    spinToken: number
    onOpenDone?: () => void
    orient: React.MutableRefObject<Orient>
}

const PackModel = ({ frontArt, backArt, spinToken, onOpenDone, orient }: ModelProps) => {
    const group = useRef<THREE.Group>(null)
    const { scene, animations, materials } = useGLTF(packModelUrl)
    const { actions, mixer } = useAnimations(animations, group)

    const [frontTex, backTex] = useTexture([frontArt, backArt])

    // Paint the chosen art onto the front and back materials. glTF authors its
    // textures with flipY=false and sRGB colour; a texture loaded by any other
    // path defaults the other way, which lands the art upside-down and washed
    // out -- so both are set explicitly here. A little emissive from the same
    // map keeps the art reading bright regardless of the lighting angle, so the
    // pack never looks muddy as it turns.
    //
    // Done during render, NOT in an effect. An effect runs after commit, and the
    // r3f render loop had already drawn the pack by then -- so on every load the
    // model appeared for a beat wearing the texture baked into the GLB, then
    // visibly swapped to the BR1 art. Both `useGLTF` and `useTexture` suspend,
    // so by the time this line runs the textures are loaded and the first frame
    // ever drawn is already the right one.
    useMemo(() => {
        const paint = (name: string, tex: THREE.Texture) => {
            const mat = materials[name] as THREE.MeshStandardMaterial | undefined
            if (!mat) return
            tex.flipY = false
            tex.colorSpace = THREE.SRGBColorSpace
            tex.anisotropy = 8
            tex.needsUpdate = true
            mat.map = tex
            mat.emissive = new THREE.Color(0xffffff)
            mat.emissiveMap = tex
            mat.emissiveIntensity = 0.28
            // A touch less metal / a touch smoother so more light comes back.
            mat.metalness = Math.min(mat.metalness ?? 1, 0.35)
            mat.needsUpdate = true
        }
        paint(FRONT_MATERIAL, frontTex)
        paint(BACK_MATERIAL, backTex)
    }, [materials, frontTex, backTex])

    // Play the baked turntable once per spinToken. Clamp on the last frame so
    // the pack settles facing the viewer rather than snapping back to frame 0.
    useEffect(() => {
        if (spinToken <= 0) return
        orient.current.opening = true
        const action = actions[OPEN_CLIP]
        if (!action) {
            onOpenDone?.()
            return
        }
        action.reset()
        action.setLoop(THREE.LoopOnce, 1)
        action.clampWhenFinished = true
        action.play()
        const onFinished = () => onOpenDone?.()
        mixer.addEventListener('finished', onFinished)
        return () => mixer.removeEventListener('finished', onFinished)
    }, [spinToken])

    // The Slabz motion, which is not a turntable.
    //
    // It used to drift one way forever at a constant rate. That reads as a
    // display stand: nothing about it says the thing can be picked up, and a
    // constant angular rate is the one motion no real object makes. Slabz sways
    // it left and right and bobs it slightly -- `sin(t * 0.8) * 0.28` -- which
    // shows both faces and the depth between them without ever completing a
    // turn, so the pack reads as being held rather than rotated.
    //
    // Three states share this node and must not fight over it:
    //
    //  * dragging   -- the finger owns the angle outright.
    //  * coasting   -- the flick is still being spent, decaying ~4%/frame at
    //                  60fps. Below a knee it stops rather than crawling.
    //  * idle       -- the sway, faded IN over half a second from wherever the
    //                  pack was left. Without that fade the pack jumps to the
    //                  sine's current value the moment a drag ends.
    useFrame((state, dt) => {
        const o = orient.current
        const g = group.current
        if (!g) return

        if (o.dragging || o.opening) {
            o.idleFor = 0
            o.restY = o.rotY
        } else {
            // Spend the flick first; the sway only starts once it is gone.
            if (Math.abs(o.velY) > 0.001) {
                o.rotY += o.velY * dt
                o.velY *= Math.pow(0.96, dt * 60)
                if (Math.abs(o.velY) < 0.02) o.velY = 0
                o.idleFor = 0
                o.restY = o.rotY
            } else {
                // The sway is RELATIVE to where the pack was left, never an
                // absolute angle.
                //
                // It used to blend towards `sin(t) * 0.28`, which is a position
                // near zero. Spin the pack three times round and that target is
                // still near zero -- so the pack unwound everything the player
                // had just done, fast, in the opposite direction. The more they
                // span it the further it rewound, which is exactly backwards
                // from an object that has been put down.
                //
                // Anchoring the sine to the resting angle, and starting its
                // phase from the moment the pack came to rest, means it begins
                // at exactly zero offset: the pack stays where it was left and
                // breathes around that.
                if (o.idleFor === 0) o.idlePhase = state.clock.getElapsedTime()
                o.idleFor += dt
                const t = state.clock.getElapsedTime() - o.idlePhase
                // Eased in, so the breath arrives rather than starting at full
                // amplitude the instant the flick dies.
                const blend = Math.min(o.idleFor / 0.6, 1)
                o.rotY = o.restY + Math.sin(t * 0.8) * 0.28 * blend
            }
        }

        // The bob runs ALWAYS, off the raw clock, in every state.
        //
        // It used to live inside the idle branch and be multiplied by the same
        // fade-in as the sway. So it froze wherever it happened to be the
        // instant a drag started, and when the drag ended the fade restarted
        // from zero -- snapping the pack vertically, then easing back. That is
        // the jump: not the spin, but the bob being switched off and on under
        // it. Nothing about picking the pack up should stop it breathing, and a
        // continuous sine has no seam to jump across.
        g.position.y = Math.sin(state.clock.getElapsedTime() * 0.8) * 0.02

        g.rotation.y = o.rotY
        g.rotation.x += (o.rotX - g.rotation.x) * 0.15
    })

    // The authored node sits off-origin; Center drops it back onto (0,0,0) so
    // the camera framing does not depend on the export's pivot.
    return (
        <group ref={group}>
            <Center>
                <primitive object={scene} />
            </Center>
        </group>
    )
}

type Props = {
    frontArt?: string
    backArt?: string
    spinToken?: number
    onOpenDone?: () => void
    className?: string
}

/**
 * The pack as a real 3D model on the stage, replacing the flat foil card.
 *
 * Drag to spin it around; opening is left to the Spin button. Lit by a small
 * procedural studio (brand-coloured Lightformers) plus a front fill and a lifted
 * exposure, so the foil catches cyan/magenta highlights and the art reads bright
 * without an HDR fetch the app's CSP would block. Transparent canvas: it floats
 * on whatever dark stage sits behind it.
 */
export const Pack3D = ({ frontArt = pack3Front, backArt = pack3Back, spinToken = 0, onOpenDone, className }: Props) => {
    const key = useMemo(() => `${frontArt}|${backArt}`, [frontArt, backArt])

    const orient = useRef<Orient>({
        rotY: 0,
        rotX: 0,
        dragging: false,
        opening: false,
        velY: 0,
        idleFor: 0,
        restY: 0,
        idlePhase: 0,
    })
    const [hint, setHint] = useState(true) // show the "drag" hint until first touch
    // `lastT` and `vel` are what turn a drag into a throw: the release keeps the
    // speed the finger was moving at, rather than stopping dead.
    const p = useRef({ active: false, lastX: 0, lastY: 0, lastT: 0, vel: 0 })

    const onPointerDown = (e: React.PointerEvent) => {
        ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
        setHint(false)
        p.current.active = true
        p.current.lastX = e.clientX
        p.current.lastY = e.clientY
        p.current.lastT = performance.now()
        p.current.vel = 0
        orient.current.dragging = true
        // A new grab kills whatever the last flick had left, so the two cannot
        // add up into a spin the player did not ask for.
        orient.current.velY = 0
    }

    const onPointerMove = (e: React.PointerEvent) => {
        if (!p.current.active) return
        const now = performance.now()
        const dx = e.clientX - p.current.lastX
        const dy = e.clientY - p.current.lastY
        const dtMs = Math.max(now - p.current.lastT, 1)
        p.current.lastX = e.clientX
        p.current.lastY = e.clientY
        p.current.lastT = now
        const step = dx * 0.012
        orient.current.rotY += step
        // Radians per second, smoothed. Raw per-event deltas are far too spiky
        // to release on -- one stuttered frame at the end would fling the pack.
        p.current.vel = p.current.vel * 0.7 + (step / (dtMs / 1000)) * 0.3
        orient.current.rotX = THREE.MathUtils.clamp(orient.current.rotX + dy * 0.006, -0.55, 0.55)
    }

    const endPointer = () => {
        if (p.current.active) {
            // Capped, so a frantic swipe cannot spin it into a blur.
            orient.current.velY = THREE.MathUtils.clamp(p.current.vel, -8, 8)
            orient.current.restY = orient.current.rotY
            orient.current.idleFor = 0
        }
        p.current.active = false
        p.current.vel = 0
        orient.current.dragging = false
    }

    return (
        <div
            className={className}
            style={{ width: '100%', height: '100%', position: 'relative', touchAction: 'none', cursor: 'grab' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerLeave={endPointer}
            onPointerCancel={endPointer}
        >
            <Canvas
                dpr={[1, 2]}
                gl={{ antialias: true, alpha: true, toneMappingExposure: 1.25 }}
                camera={{ position: [0, 0, 2.0], fov: 32 }}
            >
                <ambientLight intensity={1.0} />
                <directionalLight position={[3, 4, 5]} intensity={2.2} />
                <directionalLight position={[0, 1, 6]} intensity={1.3} />
                <directionalLight position={[-4, 1, 2]} intensity={0.8} color={'#03CEFF'} />
                <Suspense fallback={null}>
                    {/* No `<Float>` here any more.
                        It was adding a SECOND vertical motion -- its own sine,
                        at its own speed -- on top of the bob the model now
                        drives itself. Two unrelated sines on one axis do not
                        read as a bob; they beat against each other, which is
                        what made the movement look irregular next to Slabz. The
                        pack owns its own sway and bob now, exactly as Slabz's
                        does, so this wrapper is one animation too many. */}
                    <PackModel
                        key={key}
                        frontArt={frontArt}
                        backArt={backArt}
                        spinToken={spinToken}
                        onOpenDone={onOpenDone}
                        orient={orient}
                    />
                    {/* Procedural studio: a bright soft key overhead, and
                        cyan/magenta side cards so the foil reads as brand-lit metal. */}
                    <Environment resolution={256} frames={1}>
                        <Lightformer intensity={3.4} position={[0, 3, 3]} scale={[4, 4, 1]} color={'#ffffff'} />
                        <Lightformer intensity={2.0} position={[-4, 0, 2]} scale={[2, 5, 1]} color={'#03CEFF'} />
                        <Lightformer intensity={2.0} position={[4, 0, 2]} scale={[2, 5, 1]} color={'#C91FFF'} />
                        <Lightformer intensity={1.4} position={[0, -3, 1]} scale={[4, 2, 1]} color={'#ffffff'} />
                    </Environment>
                </Suspense>
            </Canvas>

            {/* First-touch hint. */}
            {hint && (
                <div className='pointer-events-none absolute inset-x-0 bottom-2 flex justify-center'>
                    <span className='text-2xs font-bold uppercase tracking-[0.2em] text-white/45'>Drag to spin</span>
                </div>
            )}
        </div>
    )
}
